import React, { useState, useEffect } from "react";
import { Button } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { CircularLoader } from "../../../components/widgets/CircularLoader";
import { ErrorText } from "../../../components";
import { doc, setDoc, updateDoc } from "firebase/firestore";
import { storage, db } from "../../../config/firebase";
import { COLLECTION } from "../../../data/constant";
import icon from "../../../assets/Images/profile.png";
import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
} from "firebase/storage";
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  serverTimestamp,
} from "firebase/firestore";
import Box from "@mui/material/Box";

/* ===================== STYLES (UNCHANGED VISUALLY) ===================== */

const styles = {
  root: {
    display: "flex",
    flexDirection: { xs: "column", sm: "row" },
    alignItems: { xs: "center", sm: "flex-start" },
    width: "100%",
    margin: "0 auto",
    padding: { xs: "10px", sm: "20px" },
    fontFamily: "montserrat",
  },
  leftSide: {
    flex: 1,
    marginRight: { xs: 0, sm: "20px" },
    marginBottom: { xs: "20px", sm: 0 },
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    fontFamily: "montserrat",
  },
  rightSide: {
    flex: 2,
    marginLeft: { xs: 0, sm: "20px" },
    display: "flex",
    flexDirection: "column",
    fontFamily: "montserrat",
  },
  box: {
    border: "1px solid #ccc",
    padding: "20px",
    borderRadius: "8px",
    marginBottom: "20px",
    width: { xs: "95%", sm: "600px" },
    fontFamily: "montserrat",
  },
  boxLeft: {
    border: "1px solid #ccc",
    padding: "20px",
    borderRadius: "8px",
    marginBottom: "20px",
    width: { xs: "95%", sm: "300px" },
    fontFamily: "montserrat",
  },
  boxLeftProfile: {
    border: "1px solid #ccc",
    padding: "20px",
    borderRadius: "8px",
    marginBottom: "20px",
    textAlign: "center",
    width: { xs: "95%", sm: "300px" },
    fontFamily: "montserrat",
  },
  userDetails: {
    marginTop: "20px",
    textAlign: "left",
    fontFamily: "montserrat",
  },
  actionBtn: {
    marginTop: "10px",
  },
  horizontalLine: {
    borderTop: "1px solid #ccc",
    margin: "10px 0",
  },
  inputLabel: {
    marginRight: "10px",
    marginTop: "10px",
    display: "block",
  },
  inputField: {
    marginBottom: "10px",
  },
  profileIcon: {
    height: "100px",
  },
};

/* ===================== COMPONENT ===================== */

const Layout = ({
  userData,
  userId,
  loading,
  snackPreferences,
  userPhoneNumber,
}) => {
  const [isEdit, setIsEdit] = useState(false);
  const [editedUserData, setEditedUserData] = useState(userData);
  const [profilePicture, setProfilePicture] = useState(null);
  const [profileImageUrl, setProfileImageUrl] = useState(null);
  const [newSnacksInput, setNewSnacksInput] = useState("");

  useEffect(() => {
    const fetchProfileImage = async () => {
      try {
        if (userData?.phoneNumber) {
          const imageRef = ref(
            storage,
            `userPhotos/${userData.phoneNumber}.jpg`
          );
          const url = await getDownloadURL(imageRef);
          setProfileImageUrl(url);
        }
      } catch {
        setProfileImageUrl(null);
      }
    };
    fetchProfileImage();
  }, [userData?.phoneNumber]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditedUserData((prev) => ({ ...prev, [name]: value }));
  };

  const handleProfilePictureUpload = async () => {
    if (!profilePicture) return;
    const imageRef = ref(
      storage,
      `userPhotos/${editedUserData.phoneNumber}.jpg`
    );
    const uploadTask = await uploadBytesResumable(
      imageRef,
      profilePicture
    );
    const url = await getDownloadURL(uploadTask.snapshot.ref);
    await setDoc(
      doc(db, COLLECTION.USER, userId),
      { photoURL: url },
      { merge: true }
    );
  };

  const handleSaveChanges = async () => {
    await updateDoc(doc(db, COLLECTION.USER, userId), editedUserData);

    if (newSnacksInput.trim()) {
      const q = query(
        collection(db, COLLECTION.SNACKPREFERENCES),
        where("phoneNumber", "==", userPhoneNumber)
      );
      const snap = await getDocs(q);

      const snackData = {
        phoneNumber: userPhoneNumber,
        snacks: newSnacksInput.trim(),
        timestamp: serverTimestamp(),
      };

      snap.empty
        ? await addDoc(
            collection(db, COLLECTION.SNACKPREFERENCES),
            snackData
          )
        : await setDoc(snap.docs[0].ref, snackData);
    }

    await handleProfilePictureUpload();
    setIsEdit(false);
  };

  if (loading) return <CircularLoader />;
  if (!userData) return <ErrorText />;

  return (
    <Box sx={styles.root}>
      {/* LEFT */}
      <Box sx={styles.leftSide}>
        <Box sx={styles.boxLeftProfile}>
          <img
            src={profileImageUrl || icon}
            alt="Profile"
            className={!profileImageUrl ? styles.profileIcon : undefined}
            style={{ width: "100px", borderRadius: "80px" }}
          />

          {isEdit && (
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setProfilePicture(e.target.files[0])}
              style={{ marginTop: "10px" }}
            />
          )}

          <Box sx={styles.actionBtn}>
            {isEdit ? (
              <Button
                variant="contained"
                onClick={handleSaveChanges}
                sx={{ backgroundColor: "black" }}
              >
                Save
              </Button>
            ) : (
              <Button
                variant="outlined"
                onClick={() => setIsEdit(true)}
                sx={{ marginLeft: "250px" }}
              >
                <EditIcon />
              </Button>
            )}
          </Box>
        </Box>

        {/* SOCIAL */}
        <Box sx={styles.boxLeft}>
          <Box sx={styles.userDetails}>
            <h3>Social Media</h3>
            {["website", "github", "twitter", "instagram", "facebook"].map(
              (field) =>
                isEdit ? (
                  <div key={field}>
                    <label style={styles.inputLabel}>{field}</label>
                    <input
                      type="text"
                      name={field}
                      value={editedUserData[field] || ""}
                      onChange={handleInputChange}
                    />
                  </div>
                ) : (
                  <div key={field}>
                    <p>
                      {field}:{" "}
                      <a href={userData[field]}>{userData[field]}</a>
                    </p>
                    <hr style={styles.horizontalLine} />
                  </div>
                )
            )}
          </Box>
        </Box>
      </Box>

      {/* RIGHT */}
      <Box sx={styles.rightSide}>
        <Box sx={styles.box}>
          <Box sx={styles.userDetails}>
            <h3>User Details</h3>

            {isEdit ? (
              <>
               {["userName", "phoneNumber", "email", "gender", "address"].map(
  (field) => (
    <div key={field} style={styles.inputField}>
      <label style={styles.inputLabel}>{field}</label>

      {field === "email" ? (
        <input
          type="text"
          name="email"
          value={editedUserData.email || ""}
          disabled
          style={{ backgroundColor: "#f5f5f5", cursor: "not-allowed" }}
        />
      ) : (
        <input
          type="text"
          name={field}
          value={editedUserData[field] || ""}
          onChange={handleInputChange}
        />
      )}
    </div>
  )
)}

                <label style={styles.inputLabel}>
                  Snack Preferences (comma-separated)
                </label>
                <input
                  type="text"
                  value={newSnacksInput}
                  onChange={(e) => setNewSnacksInput(e.target.value)}
                />
              </>
            ) : (
              <>
                <p>Name: {userData.userName}</p>
                <hr style={styles.horizontalLine} />
                <p>Phone: {userData.phoneNumber}</p>
                <hr style={styles.horizontalLine} />
                <p>Email: {userData.email}</p>
                <hr style={styles.horizontalLine} />
                <p>Gender: {userData.gender}</p>
                <hr style={styles.horizontalLine} />
                <p>Address: {userData.address}</p>
                <hr style={styles.horizontalLine} />
                <p>Snack Preferences:</p>
                {snackPreferences.length === 0
                  ? "No snacks added yet."
                  : snackPreferences.map((s, i) => <li key={i}>{s}</li>)}
              </>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Layout;
